import { FixingMode } from "@/data/fixingMode";
import { z } from "zod";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteLabelField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { ExternalLink } from "@/components/ExternalLink";
import { useEditForm } from "@/features/Edit/hooks";
import { fieldTranslations } from "./translations";
import { computeC3FixingMode } from "./utils";
import { useMemo } from "react";

const FormSchema = z
  .object({
    date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
    c3_static_datum_id: z.coerce.number().nullable().optional(),
    c3_static_datum_name: z.string().nullable().optional(),
    c3_static_datum_asset_class_1: z.string().nullable().optional(),
    fixing: z.coerce.number().nullable().optional(),
    market_value: z.coerce.number().nullable().optional(),
    aum: z.preprocess(
      (val) => (val === "" ? null : val),
      z.coerce.number().nullable().optional(),
    ),
    aum_currency: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    // kept in schema so the query cache (TechnicalInfoCard) can read them
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    time_serie_file_id: z.number().nullable().optional(),
    c3_time_serie_file_id: z.number().nullable().optional(),
  })
  .refine((data) => data.aum == null || !!data.aum_currency, {
    message: "AUM currency is required when AUM is set",
    path: ["aum_currency"],
  });

export const formFields = FormSchema.keyof().enum;

export function C3TimeSeriesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({ id, client, table_name, FormSchema });

  const currencyOptions = useCurrencyOptions();

  const c3_static_datum_id = form.watch(formFields.c3_static_datum_id);
  const asset_class_1 = form.watch(formFields.c3_static_datum_asset_class_1);

  const fixingMode: FixingMode | null = useMemo(
    () => computeC3FixingMode(asset_class_1 ?? undefined),
    [asset_class_1],
  );

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("time_series.price_valuation", {
                  defaultValue: "Price / valuation",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <InputField
                  name={formFields.c3_static_datum_name}
                  label={t(`${table_name}.columns.c3_static_datum_name`, {
                    ns: "tables",
                    defaultValue: "Asset",
                  })}
                  control={form.control}
                  fieldOptions={{ isDisabled: true }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formFields.date}
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.aum}
                    label={t(`${table_name}.columns.aum`, {
                      ns: "tables",
                      defaultValue: "AUM",
                    })}
                    control={form.control}
                    type="number"
                  />
                  <AutocompleteLabelField
                    name={formFields.aum_currency}
                    label={t(`${table_name}.columns.aum_currency`, {
                      ns: "tables",
                      defaultValue: "AUM Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                  />
                </div>
                <div className={`grid grid-cols-1 gap-4 ${fixingMode === null ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                  {fixingMode !== FixingMode.VALORISATION && (
                    <InputField
                      name={formFields.fixing}
                      label={t(`${table_name}.columns.fixing`, {
                        ns: "tables",
                        defaultValue: "Unit price",
                      })}
                      isRequired={fixingMode === FixingMode.FIXING}
                      control={form.control}
                      type="number"
                    />
                  )}
                  {fixingMode !== FixingMode.FIXING && (
                    <InputField
                      name={formFields.market_value}
                      label={t(`${table_name}.columns.market_value`, {
                        ns: "tables",
                        defaultValue: "Market value",
                      })}
                      isRequired={fixingMode === FixingMode.VALORISATION}
                      control={form.control}
                      type="number"
                    />
                  )}
                  <AutocompleteLabelField
                    name={formFields.currency}
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                    fieldOptions={{ isDisabled: true }}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <div className="absolute top-14 right-14 2xl:top-10 2xl:right-10">
          {c3_static_datum_id && (
            <Button
              href={toWorkspacePath(
                `/admin/${Clients.C3}/${Tables.C3_STATIC_DATA}/${c3_static_datum_id}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              {t("transactions.asset", { defaultValue: "Asset" })}
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(
                  changedFields,
                  ["c3_static_datum_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
